import type { HarmonyPackageDocument } from '@rhythmic/db/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import type { Hookable } from 'hookable'
import type { Ref } from 'vue'

import type { DocumentContextEvents } from '../useDocumentContext.events.schema'

type DocumentContextHookMap = {
  [K in keyof DocumentContextEvents]: (event: DocumentContextEvents[K]) => void | Promise<void>
}

/**
 * Handle for removing a previously registered document context hook.
 */
type DocumentContextUnsubscribe = () => void

interface DocumentContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface UseDocumentContextOnContextChangedParams {
  hooks: Hookable<DocumentContextHookMap>
  logger: DocumentContextLoggerLike
  currentDocumentQuery: Ref<UseLiveQueryReturn<HarmonyPackageDocument> | undefined>
  clearDocumentScopedQueries: () => void
  createDocumentQuery: (packageId: string, documentId: string) => UseLiveQueryReturn<HarmonyPackageDocument> | undefined
}

/**
 * Internal handler: sets up the document query when package/document identifiers change.
 */
export default function useDocumentContextOnContextChanged({
  hooks,
  logger,
  currentDocumentQuery,
  clearDocumentScopedQueries,
  createDocumentQuery,
}: UseDocumentContextOnContextChangedParams) {
  let activeQueryKey: string | null = null

  const unsubscribe: DocumentContextUnsubscribe = hooks.hook(
    'ContextChanged',
    ({ packageId, previousPackageId, documentId, previousDocumentId }) => {
      logger.debug('Document context changed, updating document query', {
        previousPackageId,
        packageId,
        previousDocumentId,
        documentId,
      })

      if (!packageId || !documentId) {
        if (activeQueryKey !== null) {
          logger.debug('Clearing document context query: missing packageId or documentId', {
            packageId,
            documentId,
          })
        }

        activeQueryKey = null
        clearDocumentScopedQueries()
        return
      }

      const nextKey = `${packageId}:${documentId}`
      if (activeQueryKey === nextKey) return

      activeQueryKey = nextKey
      currentDocumentQuery.value = createDocumentQuery(packageId, documentId)
    },
  )

  return unsubscribe
}
